/**
 * @docs docs/features/website-navigation-shell
 */

/*----------------------------------
- FOOTER
----------------------------------*/

import React from 'react';
import { DATABASE_SEARCH_ACTIONS } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import {
    VISIBLE_CONTEXTUAL_SEARCH_PERSONAS,
    type ContextualSearchVisiblePersonaIntent,
} from '@uniquedomains/components/contracts/upgrade/catalogs/contextualSearchConversion';
import { PersonaCard } from '@uniquedomains/components/marketing/persona-card/PersonaCard';
import useContext from '@/client/context';
import useProductSession from '@/client/hooks/useProductSession';
import { resolveProductOrigin } from '@/client/services/conversion/product/productOrigin';
import Icon from '@/client/components/ui/icon/Icon';
import {
    HOME_FINAL_CTA_PERSONA_CARDS,
    LANDING_PERSONAS,
    LANDING_DOMAINS_FOOTER_ITEMS,
    LANDING_PRODUCT_FOOTER_ITEMS,
    LANDING_SOLUTION_FOOTER_ITEMS,
} from '@/client/catalogs/landing/pageContent';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { FOOTER_COMPARE_PAGE_REGISTRY, comparePagePath } from '@/common/catalogs/landing/comparePages';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { isPublishedHumanPageTarget } from '@/common/catalogs/landing/internalLinks';
import { WORLD_FOOTER_COPY } from '@/client/catalogs/landing/worldFooter';
import { START_WITH_SEARCH_LABEL } from '@/client/catalogs/landing/accessOffer';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { toAbsoluteProductUrl } from '@/client/utils/payment/productUrls';
import { encodeRadarFiltersUrl } from '@/common/libs/filters';
import {
    DOMAINING_NEWS_FEED_PATH,
    DOMAINING_NEWS_INDEX_PATH,
} from '@uniquedomains/components/contracts/news/public';
import type { Links } from '../../types';
import FooterColumn from './FooterColumn';
import { WORLD_FOOTER_NIGHT_SCENE } from './footerScene';
import WorldFooter, { type WorldFooterStats } from './WorldFooter';
import { HEADER_LOGIN_TRIGGER_BY_PAGE, HEADER_OPEN_APP_LABEL, HEADER_PRIMARY_TRIGGER_BY_PAGE, resolveHeaderTriggerId } from './Header';
import { HOME_FINAL_ATTRIBUTION, resolveFooterPersonaCta, type FooterPersonaCta } from './footerPersona';

/*----------------------------------
- HELPERS
----------------------------------*/

function resolveFooterHref(href: string, sectionHrefPrefix: string): string {
    if (!href.startsWith('#') || !sectionHrefPrefix) {
        return href;
    }

    return `${sectionHrefPrefix}${href}`;
}

/**
 * Home's closing sign-in, as the shipped Home final card built it: a login whose return is the Product search seeded
 * with the first persona's default prompt, attributed to the final call to action.
 */
const buildHomeFinalSignInHref = (): string =>
    buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'login',
        returnTo: toAbsoluteProductUrl(encodeRadarFiltersUrl({ prompt: LANDING_PERSONAS[0].defaultPrompt }, { surface: 'product' })),
        feature: 'databaseAccess',
        action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
        from: HOME_FINAL_ATTRIBUTION,
    });

/*----------------------------------
- TYPES
----------------------------------*/

export type FooterProps = {
    links: Links;
    sectionHrefPrefix?: string;
    trackingPage?: string;
    /**
     * The server figures the sky reads (the domains count, the members count), passed by a page whose data carries
     * them (Home). Absent, the headline reads the catalog's fallback and the members line is left out.
     */
    stats?: WorldFooterStats;
    /**
     * Home's closing (batch 7, C4): the two persona cards fire Home's frozen final persona ids and attribution, and the
     * sign-in link is the one Home's journey reads (`landing-home-footer-sign-in`, the `website_landing_final_sign_in`
     * id). A prop rather than a URL check, so the frame never guesses which page it is on. Every other page draws the
     * same two cards with the frame's own ids (`footerPersona.ts`, mock parity F2, letter E21 A of 2026-11-04).
     */
    homeClosing?: boolean;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The thin owner of the world footer: the link groups from the existing catalogs (Domains gated by publication,
 * Product, Solutions, Access, Compare, Legal), their phone accordion state, and every click's tracking. `WorldFooter`
 * draws them on the night launch (2026-11-04).
 * @rule The link groups keep the shipped catalogs and take the page template's additions and renames (Gaetan's letter
 *       E17 1C of 2026-11-04, mock parity W3): the template's six short columns alone would drop About, Domaining
 *       News, Agenda, News RSS, Newsletter, Glossary, the AI sources, the comparisons hub and most listing pages from
 *       the frame's crawlable links. "For studios" left with its page (E8). The API page is listed once, as the
 *       template's Product "API" (it was Access "API Docs"). Login lives here and in the phone menu, never in the
 *       desktop bar: the Access column's Log in
 * (`footer-login-link`) and the sky's sign-in link both fire the page's own `*_header_login` id, told apart by section.
 * @rule The two persona cards sit on the sand of every page (the page template draws them, F2): a signed-in member sees
 *       them as Home shows them, since the cards are a persona choice and not a second Start row, but their button
 *       opens the app with no trigger (a member is never invited to sign up again); the Access rows read the session
 *       the same way (E22 4A).
 */

export function Footer({ links, sectionHrefPrefix = '', trackingPage = 'landing', stats, homeClosing = false }: FooterProps) {
    const { enforceDomainPublicationLinks, publicOrigins, renderedDomainResourcePaths } = useContext();
    // The Access rows read the session as the header does (decision E22 4A, R5-W3 W3-5): a member's "Start with my search"
    // becomes the header's "Open app" with no trigger, and Log in leaves, so the try-free and login counts stay intent
    // from visitors only. The probe is the header's one request per document (`useProductSession` shares it); the
    // server renders the visitor's rows, as it does the header's.
    const { status: productSessionStatus } = useProductSession(publicOrigins.productOrigin);
    const isMember = productSessionStatus === 'member';
    const productHomeHref = `${resolveProductOrigin(publicOrigins.productOrigin)}/`;
    const publishedDomainPaths = enforceDomainPublicationLinks
        ? renderedDomainResourcePaths
        : undefined;
    const publishedDomainFooterItems = LANDING_DOMAINS_FOOTER_ITEMS.filter((item) =>
        isPublishedHumanPageTarget(item.href, publishedDomainPaths),
    );
    // Phone accordion: one group open at a time. Desktop ignores it, every column is shown.
    const [openColumn, setOpenColumn] = React.useState<string | null>(null);
    const columnState = (title: string) => ({
        isOpen: openColumn === title,
        onToggle: () => setOpenColumn((current) => (current === title ? null : title)),
    });
    const loginTriggerId = resolveHeaderTriggerId(trackingPage, HEADER_LOGIN_TRIGGER_BY_PAGE, 'website_landing_header_login');
    /*
     * The site's login controls outside the phone menu, since Login left the desktop bar on 2026-09-17. They fire the
     * page's own `*_header_login` id rather than a footer id, so the login intent stays one row per page in the funnel
     * wherever it is clicked, told apart by `section`. The footer-scoped `*_footer_sign_in` ids stay in the shared
     * catalog, which Product pins, and do not fire here.
     */
    const trackLogin = (section: 'footer_access' | 'footer_cta', label: string) => {
        trackLandingCta({ page: trackingPage, section, label, kind: 'auth', href: links.signIn, surface: 'footer' });
        trackConversionTrigger(loginTriggerId, { href: links.signIn, page: trackingPage, section });
    };
    const primaryTriggerId = resolveHeaderTriggerId(trackingPage, HEADER_PRIMARY_TRIGGER_BY_PAGE, 'website_landing_header_try_free');
    // The template's Access "Start with my search" (E17 1C) is the header CTA's link and id, told apart by section.
    const trackStart = () => {
        trackLandingCta({ page: trackingPage, section: 'footer_access', label: START_WITH_SEARCH_LABEL, kind: 'product', href: links.productBaseUrl, surface: 'footer' });
        trackConversionTrigger(primaryTriggerId, { href: links.productBaseUrl, page: trackingPage, section: 'footer_access' });
    };
    // A member's click is navigation, not conversion: no trigger id, as the header's Open app.
    const trackOpenApp = () =>
        trackLandingCta({ page: trackingPage, section: 'footer_access', label: HEADER_OPEN_APP_LABEL, kind: 'product', href: productHomeHref, surface: 'footer' });
    const trackAiSource = (target: 'llms' | 'security') => track('ai_source_clicked', { surface: 'footer', target, page: trackingPage });
    // Home's record keeps its shipped shape (`page` is the href, no surface); the frame's cards file the page they sit
    // on, as every other footer control does, so one id serves every page in the funnel.
    const trackPersonaCard = (persona: ContextualSearchVisiblePersonaIntent, label: string, cta: FooterPersonaCta) => {
        trackLandingCta({ page: trackingPage, section: cta.section, label, kind: 'persona', href: cta.href, ...(homeClosing ? {} : { surface: 'footer' }) });
        track('persona_card_cta_clicked', { persona, section: cta.section });
        trackConversionTrigger(cta.triggerId, { href: cta.href, page: homeClosing ? cta.href : trackingPage, section: cta.section, context: { persona } });
    };
    // A member keeps the two cards (the brief: as Home shows them) but is never invited to create the account they
    // have (docs/fixes/2026-09-14-website-header-blind-to-members.md): their card opens the app as the Access row's
    // Open app does, a navigation record with no trigger, so the two cold ids count visitors only (F2 self-review).
    const trackMemberPersonaCard = (persona: ContextualSearchVisiblePersonaIntent, label: string, section: FooterPersonaCta['section']) =>
        trackLandingCta({ page: trackingPage, section, label, kind: 'persona', href: productHomeHref, surface: 'footer' });
    const homeSignInHref = homeClosing ? buildHomeFinalSignInHref() : null;
    const signIn = homeSignInHref
        ? {
              href: homeSignInHref,
              testId: 'landing-home-footer-sign-in',
              onClick: () => {
                  trackLandingCta({ page: trackingPage, section: 'final_cta', label: WORLD_FOOTER_COPY.signInLabel, kind: 'auth', href: homeSignInHref });
                  trackConversionTrigger('website_landing_final_sign_in', { href: homeSignInHref, page: homeSignInHref, section: 'final_cta' });
              },
          }
        : { href: links.signIn, testId: 'footer-sign-in', onClick: () => trackLogin('footer_cta', WORLD_FOOTER_COPY.signInLabel) };
    const personaCards = VISIBLE_CONTEXTUAL_SEARCH_PERSONAS.map((persona) => {
        const card = HOME_FINAL_CTA_PERSONA_CARDS[persona.id];
        const cta = resolveFooterPersonaCta(persona.id, homeClosing);
        return (
            <PersonaCard
                key={persona.id}
                icon={<Icon name={persona.icon} size={20} />}
                title={persona.title}
                description={card.description}
                points={card.bullets}
                pointIcon={<Icon name="check" size={15} strokeWidth={2.5} />}
                cta={{
                    label: card.startLabel,
                    href: isMember ? productHomeHref : cta.href,
                    testId: cta.testId,
                    persona: persona.id,
                    onClick: () => (isMember ? trackMemberPersonaCard(persona.id, persona.title, cta.section) : trackPersonaCard(persona.id, persona.title, cta)),
                }}
            />
        );
    });

    return (
        <WorldFooter
            stats={stats}
            sources={WORLD_FOOTER_NIGHT_SCENE}
            personaCards={personaCards}
            brandHref={LANDING_WEBSITE_PAGE_ROUTES.home}
            githubHref={links.github}
            signIn={signIn}
        >
            <FooterColumn title="Domains" {...columnState('Domains')}>
                {publishedDomainFooterItems.map((item) => (
                    <li key={item.href}>
                        <a href={item.href}>{item.label}</a>
                    </li>
                ))}
            </FooterColumn>

            <FooterColumn title="Product" {...columnState('Product')}>
                {LANDING_PRODUCT_FOOTER_ITEMS.map((item) => (
                    <li key={item.id}>
                        <a href={resolveFooterHref(links[item.linkId], sectionHrefPrefix)}>{item.label}</a>
                    </li>
                ))}
                <li>
                    <a href={links.apiDocs}>API</a>
                </li>
            </FooterColumn>

            <FooterColumn title="Solutions" {...columnState('Solutions')}>
                {LANDING_SOLUTION_FOOTER_ITEMS.map((item) => (
                    <li key={item.id}>
                        <a href={links[item.linkId]}>{item.footerLabel}</a>
                    </li>
                ))}
                <li>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.team}>About</a>
                </li>
                <li>
                    <a href={links.pricing}>Pricing</a>
                </li>
            </FooterColumn>

            <FooterColumn title="Access" {...columnState('Access')}>
                <li hidden={isMember}>
                    <a href={links.signIn} onClick={() => trackLogin('footer_access', 'Log in')} data-testid="footer-login-link">
                        Log in
                    </a>
                </li>
                <li>
                    <a href={`${links.pricing}#plans`}>Pricing</a>
                </li>
                <li>
                    {isMember ? (
                        <a href={productHomeHref} onClick={trackOpenApp} data-testid="footer-open-app-link">
                            {HEADER_OPEN_APP_LABEL}
                        </a>
                    ) : (
                        <a href={links.productBaseUrl} onClick={trackStart} data-testid="footer-start-link">
                            {START_WITH_SEARCH_LABEL}
                        </a>
                    )}
                </li>
                <li>
                    <a href={DOMAINING_NEWS_INDEX_PATH}>Domaining News</a>
                </li>
                {/* The Market menu's dated page, beside the news it shares that menu with. */}
                <li>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.agenda}>Agenda</a>
                </li>
                <li>
                    <a href={DOMAINING_NEWS_FEED_PATH}>News RSS</a>
                </li>
                <li>
                    <a href={links.newsletter}>Newsletter</a>
                </li>
                <li>
                    <a href={links.glossary}>Glossary</a>
                </li>
                <li>
                    <a href="/llms.txt" onClick={() => trackAiSource('llms')}>AI source</a>
                </li>
                <li>
                    <a href="/llms-security.txt" onClick={() => trackAiSource('security')}>AI security</a>
                </li>
            </FooterColumn>

            <FooterColumn title="Compare" testId="footer-compare" {...columnState('Compare')}>
                <li>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.compare}>All comparisons</a>
                </li>
                {FOOTER_COMPARE_PAGE_REGISTRY.map((entry) => (
                    <li key={entry.slug}>
                        <a href={comparePagePath(entry.slug)}>{entry.footerLabel || entry.competitorShortName}</a>
                    </li>
                ))}
            </FooterColumn>

            <FooterColumn title="Legal" testId="footer-legal" {...columnState('Legal')}>
                <li>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.terms}>Terms</a>
                </li>
                <li>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.privacy}>Privacy</a>
                </li>
                <li>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.refundPolicy}>Refund policy</a>
                </li>
            </FooterColumn>
        </WorldFooter>
    );
}
